import { SyntheticEvent, useState } from 'react';
import styled from '@emotion/styled';
import { Checkbox, Modal, Button, theme } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import { SHELL_OVERLAY_Z_INDEX } from 'constants/zIndex';

type CancelAutoBookingsModalProps = {
  open: boolean;
  reservationCount: number;
  /** Localized date automatic booking resumes on (advanced-booking horizon). */
  resumeDate: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (shouldCancelVisits: boolean) => void;
};

/**
 * Destructive confirmation for cancelling every upcoming auto-booked desk
 * reservation. Ported from the legacy profile page's ADBCancelModal
 * (robin-dashboard appv2), restyled to ui-kit like the sibling
 * CancelReservationModal.
 */
export const CancelAutoBookingsModal = ({
  open,
  reservationCount,
  resumeDate,
  loading,
  onClose,
  onConfirm,
}: CancelAutoBookingsModalProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'desks.autoBookings',
  });
  const { token } = theme.useToken();

  // Matches the legacy modal: cancelling auto-bookings also flips the user's
  // upcoming office visits to remote unless they opt out.
  const [shouldCancelVisits, setShouldCancelVisits] = useState(true);

  return (
    <Modal
      title={t('modalTitle')}
      open={open}
      zIndex={SHELL_OVERLAY_Z_INDEX}
      onCancel={onClose}
      modalRender={(modalNode) => (
        <ModalEventBoundary
          onClick={stopModalEvent}
          onMouseDown={stopModalEvent}
          onPointerDown={stopModalEvent}
        >
          {modalNode}
        </ModalEventBoundary>
      )}
      footer={
        <>
          <Button key="keep" onClick={onClose}>
            {t('keepBookings')}
          </Button>
          <Button
            danger
            type="primary"
            key="cancel"
            loading={loading}
            onClick={() => onConfirm(shouldCancelVisits)}
          >
            {t('cancelBookings')}
          </Button>
        </>
      }
    >
      <ModalBody>
        <div>{t('modalDescription', { reservationCount, resumeDate })}</div>
        <CheckboxWell $background={token.colorFillQuaternary}>
          <Checkbox
            checked={shouldCancelVisits}
            onChange={() => setShouldCancelVisits((current) => !current)}
            data-testid="cancel-auto-bookings-visits-checkbox"
          >
            {t('updateStatusToRemote')}
          </Checkbox>
        </CheckboxWell>
      </ModalBody>
    </Modal>
  );
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// The gray well around the status checkbox, matching the legacy modal.
const CheckboxWell = styled.div<{ $background: string }>`
  background-color: ${({ $background }) => $background};
  padding: 8px;
  border-radius: 4px;
`;

const ModalEventBoundary = styled.div``;

// The modal portals inside the drawer's DOM; keep clicks from reaching the
// drawer's outside-click handling (same guard as CancelReservationModal).
const stopModalEvent = (event: SyntheticEvent<HTMLDivElement>) => {
  event.stopPropagation();
  event.nativeEvent.stopImmediatePropagation?.();
};
